/**
 * Client-side product card renderer — the one place a search-index document becomes a card.
 *
 * Both surfaces that render cards from the index use this: the search results page and the
 * directory collection page. The markup itself is not here — it lives in
 * `snippets/search-result-card-template.liquid`, so a change to the card is made in Liquid once
 * and both renderers pick it up. This file only fills the `data-card-*` hooks.
 *
 * Money never comes from the index. `SearchEngine.priceFor()` resolves the document's base price
 * against the visitor's market through the 67-tier lookup; the index stores a single base price
 * and this store runs 46 markets with separate price lists.
 *
 * @example
 *   const render = ProductCardRender.create({
 *     cardTemplate: document.querySelector('[data-search-card-template]'),
 *     starRows: document.querySelector('[data-search-star-rows]'),
 *     engine: window.SearchEngine,
 *   });
 *   grid.appendChild(render(doc));
 */
const ProductCardRender = (() => {
  // Matches `product-card.liquid`'s `image_url: width: 600`. The index stores the original upload
  // and hands back an unsized CDN URL, so without this every card downloads a full-size image for
  // a 302×240 slot — roughly double the bytes for no visible gain.
  const CARD_IMAGE_WIDTH = '600';

  // Kept in lockstep with `product-card.liquid`, so a card rendered here and one rendered in
  // Liquid resolve to the same file. 600 stays the ceiling: cards are full-width below 750px, so
  // a phone at dpr 2 already asks for more than the largest candidate, and adding bigger ones
  // would only spend bytes on the most common visit.
  const CARD_IMAGE_WIDTHS = [300, 352, 450, 600];
  const CARD_IMAGE_SIZES =
    '(min-width: 1440px) 340px, (min-width: 990px) 33vw, (min-width: 750px) 50vw, 100vw';

  /** The same CDN URL at a given width, or the URL untouched if it will not parse. */
  const atWidth = (url, width) => {
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.href;
    } catch (e) {
      return url;
    }
  };

  /** The card-sized variant of a CDN image URL, or the URL untouched if it will not parse. */
  const cardImageUrl = (url) => atWidth(url, CARD_IMAGE_WIDTH);

  /* ---------------------------------------------------------- focal points */

  // The index has no focal point, so a square upload rendered here was always centre-cropped
  // into the 302×240 slot while `product-card.liquid` honours the point set in the admin media
  // gallery. `sections/product-focal-points.liquid` lists the off-centre ones as
  // `{ pages, points: { handle: "x% y%" } }`; it is fetched through the Section Rendering API
  // because Liquid can only see 250 products per render and the catalogue is bigger. One trip
  // per tab: the merged map is kept in sessionStorage.
  const FOCAL_POINTS_URL = '/collections/all?section_id=product-focal-points';
  const FOCAL_POINTS_CACHE_KEY = 'ew-card-focal-points';

  /** handle → `object-position`, or null until the map has loaded. */
  let focalPoints = null;
  let focalPointsPromise = null;

  const fetchFocalPointsPage = async (page) => {
    const response = await fetch(`${FOCAL_POINTS_URL}&page=${page}`);
    if (!response.ok)
      throw new Error(`focal points page ${page}: ${response.status}`);

    // The section comes wrapped in its `#shopify-section-…` div; the JSON is the text inside.
    const html = await response.text();
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    return JSON.parse(body.textContent);
  };

  /** The product handle in a card URL, whatever the locale prefix or query string. */
  const handleFrom = (url) =>
    (String(url).split('?')[0].split('/products/')[1] || '').split('/')[0];

  /** Sets `object-position` on every rendered card image the loaded map has a point for. */
  const applyFocalPoints = (root) => {
    if (!focalPoints) return;

    root
      .querySelectorAll('[data-card-image][data-card-handle]')
      .forEach((image) => {
        const point = focalPoints[image.dataset.cardHandle];
        if (point) image.style.objectPosition = point;
      });
  };

  /**
   * Loads the map once per page, patching any card already rendered when it lands. Never
   * rejects: without the map every card keeps the template's `object-center`.
   */
  const loadFocalPoints = () => {
    if (focalPointsPromise) return focalPointsPromise;

    focalPointsPromise = (async () => {
      try {
        const cached = sessionStorage.getItem(FOCAL_POINTS_CACHE_KEY);
        if (cached) return JSON.parse(cached);
      } catch (e) {
        // Storage unavailable (private mode, quota) — fetch instead.
      }

      const first = await fetchFocalPointsPage(1);
      const pages = [];
      for (let page = 2; page <= (first.pages || 1); page++) {
        pages.push(fetchFocalPointsPage(page));
      }
      const rest = await Promise.all(pages);
      const points = Object.assign(
        {},
        first.points,
        ...rest.map((p) => p.points),
      );

      try {
        sessionStorage.setItem(FOCAL_POINTS_CACHE_KEY, JSON.stringify(points));
      } catch (e) {
        // Same: a cache miss next time costs a fetch, nothing else.
      }
      return points;
    })()
      .catch(() => ({}))
      .then((points) => {
        focalPoints = points;
        applyFocalPoints(document);
        return points;
      });

    return focalPointsPromise;
  };

  /** Turns a card's two photos into the box alone, as `gift-photo` draws a gift with one image. */
  const dropSecondPhoto = (card) => {
    const pane = card.querySelector('[data-card-second-pane]');
    if (pane) pane.remove();
    const dots = card.querySelector('[data-card-dots]');
    if (dots) dots.remove();
    const photo = card.querySelector('[data-card-photo]');
    if (photo) {
      photo.removeAttribute('data-swipe');
      photo.removeAttribute('data-has-second');
    }
  };

  /** A `srcset` across {@link CARD_IMAGE_WIDTHS}, or '' if the URL will not parse. */
  const cardImageSrcset = (url) => {
    try {
      new URL(url, window.location.origin);
    } catch (e) {
      return '';
    }

    return CARD_IMAGE_WIDTHS.map(
      (width) => `${atWidth(url, width)} ${width}w`,
    ).join(', ');
  };

  /**
   * Commits the deferred image URLs on cards that are now in the document.
   *
   * The renderer cannot assign `src` itself: it builds each card detached, and assigning `src` to
   * a detached `loading="lazy"` image drops the load silently — the browser defers it pending an
   * intersection check that never runs, and inserting the element later does not restart it. The
   * card then sits there with an empty image box until some unrelated scroll or resize makes the
   * browser re-evaluate. Assigning after insertion is what makes native lazy loading work at all.
   *
   * @param {ParentNode} root - The grid the cards were just appended to.
   */
  const attachImages = (root) => {
    root.querySelectorAll('[data-card-image][data-src]').forEach((image) => {
      // srcset and sizes first: assigning `src` is what starts the load, and the browser picks a
      // candidate from whatever is set at that moment. Setting them afterwards would fetch the
      // `src` and then potentially fetch a second file once the srcset landed.
      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset;
        image.sizes = CARD_IMAGE_SIZES;
        delete image.dataset.srcset;
      }

      image.src = image.dataset.src;
      delete image.dataset.src;
    });

    // A card built before the map landed and inserted after it was applied is caught here.
    applyFocalPoints(root);
  };

  /**
   * How many cards cover about two rows at the current breakpoint.
   *
   * Read off a grid element that is actually visible — a `display: none` element reports
   * `grid-template-columns: none` — so pass the skeleton, which is what is on screen at the point
   * this matters.
   *
   * @param {Element} [gridEl]
   * @returns {number}
   */
  const firstScreenful = (gridEl) => {
    if (!gridEl) return 8;

    const columns = window
      .getComputedStyle(gridEl)
      .gridTemplateColumns.split(' ')
      .filter((value) => value && value !== 'none').length;

    return Math.max(columns, 1) * 2;
  };

  /**
   * Fetches the first screenful of card images and resolves once they are in — or have failed, or
   * the deadline passes.
   *
   * Called while the skeleton is still up, so the wait for images happens behind the placeholder
   * instead of in front of a grid of empty boxes. The real `<img>` elements paint from cache a
   * moment later. Fetching here rather than on the cards themselves also sidesteps the visibility
   * problem: the grid is `display: none` behind the skeleton, where a lazy image is not
   * intersecting anything and would not load until after the reveal.
   *
   * @param {object[]} documents - Normalised documents, in render order.
   * @param {object} [options]
   * @param {number} [options.count] - How many to wait for; the rest stream in on scroll.
   * @param {number} [options.timeout] - Hard cap in ms, so a slow CDN cannot strand the shopper
   *   on skeletons.
   * @returns {Promise<void>}
   */
  const preload = (documents, { count = 8, timeout = 2500 } = {}) => {
    const sources = documents
      .slice(0, count)
      .map((doc) => doc.image)
      .filter(Boolean);

    if (!sources.length) return Promise.resolve();

    let timer;

    // `new Image()` is eager and detached, which — unlike the lazy `<img>` in the card — starts
    // fetching the moment `src` is set.
    //
    // It gets the same srcset and sizes as the card it is warming. Without them the preloader
    // would always fetch the 600w file while the card went on to choose, say, 352w — two
    // downloads per card, the second one the only one that paints. That is exactly the bug the
    // hero had with its `preload: true` header, and it is worth not rebuilding here.
    const loaded = sources.map(
      (source) =>
        new Promise((resolve) => {
          const image = new Image();
          image.onload = resolve;
          image.onerror = resolve;

          const srcset = cardImageSrcset(source);
          if (srcset) {
            image.srcset = srcset;
            image.sizes = CARD_IMAGE_SIZES;
          }

          image.src = cardImageUrl(source);
        }),
    );

    return Promise.race([
      Promise.all(loaded),
      new Promise((resolve) => {
        timer = window.setTimeout(resolve, timeout);
      }),
    ]).then(() => window.clearTimeout(timer));
  };

  /**
   * Builds a renderer bound to a pair of templates and the engine.
   *
   * @param {object} deps
   * @param {HTMLTemplateElement} deps.cardTemplate - `[data-search-card-template]`
   * @param {HTMLTemplateElement} [deps.starRows] - `[data-search-star-rows]`
   * @param {object} deps.engine - `window.SearchEngine`
   * @returns {(doc: object) => HTMLElement}
   */
  const create = ({ cardTemplate, starRows, engine }) => {
    // Kicked off here, so it runs alongside the first index query rather than after it.
    loadFocalPoints();

    /**
     * Stars are cloned from a Liquid-rendered row rather than built in JS, so the SVG paths are
     * never duplicated into JavaScript. One row exists per half point; pick the nearest.
     */
    const nearestStars = (rating) => {
      if (!starRows) return null;

      const bucket = (Math.round((Number(rating) || 0) * 2) / 2).toFixed(1);
      const row =
        starRows.content.querySelector(`[data-star-row="${bucket}"]`) ||
        starRows.content.querySelector('[data-star-row="0.0"]');

      return row ? row.firstElementChild.cloneNode(true) : null;
    };

    return (doc) => {
      const card = cardTemplate.content.firstElementChild.cloneNode(true);
      const money = engine.priceFor(doc.basePrice);

      card.querySelector('[data-card-link]').href = doc.url;
      card.querySelector('[data-card-title]').textContent = doc.title;

      const image = card.querySelector('[data-card-image]');
      const placeholder = card.querySelector('[data-card-placeholder]');

      if (doc.image) {
        // Deferred to `attachImages`, after the card is in the document — see the note there.
        image.dataset.src = cardImageUrl(doc.image);
        image.dataset.srcset = cardImageSrcset(doc.image);
        image.alt = doc.title;
        image.dataset.cardHandle = handleFrom(doc.url);

        const point = focalPoints && focalPoints[image.dataset.cardHandle];
        if (point) image.style.objectPosition = point;
      } else {
        image.remove();
        placeholder.classList.remove('hidden');
      }

      const second = card.querySelector('[data-card-image-second]');
      if (doc.image && doc.secondImage) {
        // Left in data-src for assets/ew-card-photo.js, which loads it as gift-photo's: at once
        // with a mouse, for a card someone stops at on a touch screen.
        second.dataset.src = cardImageUrl(doc.secondImage);
        second.dataset.srcset = cardImageSrcset(doc.secondImage);
        second.sizes = CARD_IMAGE_SIZES;
        // The index keeps a replaced image's link until it re-syncs, and that link answers 404:
        // such a card shows the box alone rather than swiping to an empty frame.
        second.addEventListener('error', () => dropSecondPhoto(card), {
          once: true,
        });
      } else {
        dropSecondPhoto(card);
      }

      // Subline. The template ships in the with-subline shape, so the common path is one
      // assignment; the exception (only the gift card, in a 250-document sample) drops the
      // paragraph, as `product-card.liquid` does when the metafield is blank.
      const subline = card.querySelector('[data-card-subline]');
      const sublineText = (doc.includedExperiences || '').trim();

      if (sublineText) {
        subline.textContent = sublineText;
      } else {
        subline.remove();
      }

      const points = card.querySelector('[data-card-points]');
      const pointTemplate = points.querySelector('[data-card-point]');
      if (doc.benefits.length) {
        doc.benefits.forEach((text) => {
          const point = pointTemplate.cloneNode(true);
          point.querySelector('[data-card-point-text]').textContent = text;
          points.appendChild(point);
        });
        pointTemplate.remove();
      } else {
        points.remove();
      }

      const ratingSlot = card.querySelector('[data-card-rating-slot]');
      const stars = nearestStars(doc.rating);
      if (stars && doc.rating) {
        const ratingText = stars.querySelector(
          '.rating-box > div:nth-of-type(2)',
        );
        if (ratingText) ratingText.textContent = doc.rating;

        // The review line appears on its own once `ew_pdp.review_count` is in the index; until
        // that sync lands the field is absent and the line is dropped rather than showing zero.
        const reviewText = stars.querySelector(
          '.rating-box > div:nth-of-type(3)',
        );
        if (reviewText) {
          const count = Number(doc.reviewCount);
          if (Number.isFinite(count) && count > 0) {
            reviewText.textContent = `(${count.toLocaleString()} ${
              count === 1 ? 'review' : 'reviews'
            })`;
          } else {
            reviewText.remove();
          }
        }

        ratingSlot.appendChild(stars);
      } else {
        ratingSlot.remove();
      }

      const price = card.querySelector('[data-card-price]');
      const compare = card.querySelector('[data-card-compare]');
      const badge = card.querySelector('[data-card-badge]');

      price.textContent = money ? money.price : '';

      // "From", on the products whose price is a floor — same rule as
      // `snippets/product-price.liquid`. Dropped rather than left hidden when it does not apply,
      // so the card carries no empty span.
      const from = card.querySelector('[data-card-price-from]');
      const fromLabel = money ? engine.priceFromLabel(doc) : '';

      if (fromLabel) {
        from.textContent = fromLabel;
        from.classList.remove('hidden');
      } else {
        from.remove();
      }

      // The badge ships invisible rather than hidden: `.badge` in app.scss sets
      // `display: inline-block` and app.css loads after the Tailwind output, so `.hidden` never
      // wins. Left invisible it holds the row at the badge's height, as `product-card.liquid`
      // does for a gift without a saving.
      if (money && money.compareAt) {
        compare.textContent = money.compareAt;
        compare.classList.remove('hidden');
        badge.textContent = `${money.savePercent}%`; // the dash is drawn by .ew-save (assets/ew-season.css)
        badge.classList.remove('invisible');
        badge.removeAttribute('aria-hidden');
      } else {
        compare.remove();
      }

      return card;
    };
  };

  return { create, attachImages, preload, firstScreenful, loadFocalPoints };
})();

window.ProductCardRender = ProductCardRender;
